<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { useSortByName } from '@/common/composables/sortByName';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import MainLayout from '@/common/components/MainLayout.vue';
import { Button, Column, DataTable, Card, InputText, Menu } from 'primevue';
import type { DataTableRowDoubleClickEvent, DataTableRowClickEvent } from 'primevue';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import type { MenuItem } from 'primevue/menuitem';
import { useRouter } from 'vue-router';
import { useProductionOrderStore } from 'shared/productionOrders/stores/productionOrderStore';
import type { ProductionOrder } from 'shared/productionOrders/domain/productionOrder';
import DeleteProductionOrderDialog from './components/DeleteProductionOrderDialog.vue';
import { useRefreshProductionOrderStore } from './stores/refreshProductionOrderStore';
import { useProductionOrderAuditApi } from '@/audit/api/productionorderAuditApi';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useProductionOrderStore();
const refreshStore = useRefreshProductionOrderStore();
const router = useRouter();
const auditApi = useProductionOrderAuditApi();

// Clear corrupt persisted sort state before the DataTable restores it while rendering.
clearCorruptDataTableState('production-orders-table-state');

onMounted(() => {
  refreshStore.initialize();
});

const productionOrders = useSortByName({
  list: storeToRefs(store).productionOrders,
  getName: (e) => e.name,
});

const selected = ref<ProductionOrder[]>([]);
const loading = ref(false);
const menuEntityId = ref<string>();
const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

interface ProductionOrderColumn extends ColumnField {
  sortField: string;
  cellClass: string;
}

const columnDefinitions = computed<ProductionOrderColumn[]>(() => [
  { field: 'name', header: translations.value.generic.name, sortField: 'nameSortOrder', cellClass: 'productionorder-name', filterType: 'text' },
  { field: 'priority', header: translations.value.generic.priority, sortField: 'prioritySortOrder', cellClass: 'productionorder-priority', filterType: 'numeric' },
  { field: 'plannedStartUtc', header: translations.value.productionOrders.plannedStart, sortField: 'plannedStartSortOrder', cellClass: 'productionorder-plannedstart', filterType: 'text' },
  { field: 'resource', header: translations.value.generic.resource, sortField: 'resourceSortOrder', cellClass: 'productionorder-resource', filterType: 'text' },
]);
const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector<ProductionOrderColumn>({ storageKey: 'production-orders-table-columns', columns: columnDefinitions });

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => productionOrders.value.length,
});

const menu = useTemplateRef('menu');
const deleteDialog = useTemplateRef('delete-dialog');
const auditDialog = useTemplateRef('audit-dialog');
const actionFailedAlert = useActionFailedAlert();

const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuEntityId.value;
      if (!id) return;
      show(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuEntityId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    }
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuEntityId.value;
      if (!id) return;

      const response = await auditApi.execute({
        id: id
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit
      });
    }
  }
]);

function onDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const productionOrder = ev.data as ProductionOrder;
  show(productionOrder.id);
}

function onClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.production-order-name');
  if (!name) return;
  show(ev.data.id);
}

function onAddNewClick() {
  show(null);
}

function show(id: string | null) {
  if (id) {
    router.push('/productionorder/' + id);
  } else {
    router.push('/productionorder');
  }
}

function toggleMenu(ev: Event, id: string) {
  menuEntityId.value = id;
  menu.value?.toggle(ev);
}
</script>

<template>
  <MainLayout>
    <Card class="productionorder-card">
      <template #title>
        <span>{{ translations.productionOrders.listPage.title }}</span>
        <div class="filter-info">
          <span v-if="hasActiveFilters && filteredCount < productionOrders.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ productionOrders.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
        </div>
        <Button class="add-button" @click="onAddNewClick" :label="translations.generic.add">
        </Button>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
      </template>
      <template #content>
        <DataTable class="productionorder-table" sort-mode="multiple" v-model:multiSortMeta="multiSortMeta" :class="sortBadgeClass" v-model:selection="selected"
          v-model:filters="filters" :value="productionOrders" data-key="id" scrollable scrollHeight="flex"
          meta-key-selection @row-dblclick="onDoubleClick" :loading="loading" @row-click="onClick" stateStorage="local" stateKey="production-orders-table-state" filterDisplay="menu" @filter="onFilter">
          <template #empty>{{ translations.productionOrders.listPage.noFound }}</template>
          <template #loading>{{ translations.productionOrders.listPage.loading }}</template>
          <Column v-for="col of selectedColumns" :key="col.field" :field="col.field" :class="col.cellClass"
            :header="col.header" sortable :sort-field="col.sortField"
            :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
            :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
            :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
            <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
              <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
                :options="col.filterOptions"
                :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
            </template>
          </Column>
          <Column class="w-24 !text-end">
            <template #header>
              <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
                @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
            </template>
            <template #body="productionOrder">
              <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
                @click="(ev) => toggleMenu(ev, productionOrder.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </MainLayout>

  <DeleteProductionOrderDialog ref="delete-dialog"></DeleteProductionOrderDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="menu" :model="menuItems" :popup="true"></Menu>
</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto 1rem auto;
  column-gap: 0.5rem;
  align-items: center;
}

.filter-info {
  grid-column: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.add-button {
  grid-column: 3;
}

.search-field {
  grid-column: 5;
}

:deep(.dashboard-name) {
  cursor: pointer;
}

:deep(.dashboard-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.productionorder-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
